import { ParsedCsvRow, AnalysisParams } from '@/components/user/csv-analysis/types';
import { AntiLossStreakService } from './anti-loss-streak-service';

export interface AntiLossStreakOptimizationResult {
  optimalWaitForLosses: number;
  optimalStopAfterLosses: number;
  totalProfit: number;
  maxDrawdown: number;
  profitDDRatio: number;
  winRate: number;
  totalTrades: number;
  skippedTrades: number;
  effectiveness: number; // Percentage of trades that would be protected
}

export interface AntiLossStreakTestResult extends AntiLossStreakOptimizationResult {
  waitForLosses: number;
  stopAfterLosses: number;
  status: 'optimal' | 'good' | 'poor';
  mode?: string;
}

export class AntiLossStreakOptimizer {
  /**
   * Find optimal anti loss streak protection settings with anti look-ahead bias
   */
  static findOptimalAntiLossStreak(
    csvData: ParsedCsvRow[],
    maxWaitToTest: number,
    maxStopToTest: number,
    params: AnalysisParams
  ): AntiLossStreakOptimizationResult {
    const results = this.findTopAntiLossStreakSettings(csvData, maxWaitToTest, maxStopToTest, params);
    return results.length > 0 ? results[0] : {
      optimalWaitForLosses: 1,
      optimalStopAfterLosses: 1,
      totalProfit: 0,
      maxDrawdown: 0,
      profitDDRatio: 0,
      winRate: 0,
      totalTrades: 0,
      skippedTrades: 0,
      effectiveness: 0
    };
  }

  /**
   * Find top 10 anti loss streak settings with status classification
   */
  static findTopAntiLossStreakSettings(
    csvData: ParsedCsvRow[],
    maxWaitToTest: number,
    maxStopToTest: number,
    params: AnalysisParams,
    mode?: string
  ): AntiLossStreakTestResult[] {
    // Sort data chronologically to ensure forward-looking analysis
    const sortedData = [...csvData].sort((a, b) => {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });

    const results: AntiLossStreakTestResult[] = [];

    // Test different combinations of waitForLosses and stopAfterLosses
    for (let waitForLosses = 1; waitForLosses <= maxWaitToTest; waitForLosses++) {
      for (let stopAfterLosses = 1; stopAfterLosses <= maxStopToTest; stopAfterLosses++) {
        const result = this.testAntiLossStreakSetting(
          sortedData,
          waitForLosses,
          stopAfterLosses,
          params,
          mode
        );

        // Classify the result based on performance metrics
        let status: 'optimal' | 'good' | 'poor' = 'poor';
        if (result.profitDDRatio > 2.0 && result.effectiveness > 10) {
          status = 'optimal';
        } else if (result.profitDDRatio > 1.0 && result.effectiveness > 5) {
          status = 'good';
        }

        results.push({
          ...result,
          waitForLosses,
          stopAfterLosses,
          status,
          mode: mode || params.anti_loss_streak_mode || 'consecutive_losses'
        });
      }
    }

    // Sort by performance: profit/DD ratio, effectiveness, then total profit
    return results
      .sort((a, b) => {
        // Primary: Profit/DD ratio (higher is better)
        if (Math.abs(a.profitDDRatio - b.profitDDRatio) > 0.1) {
          return b.profitDDRatio - a.profitDDRatio;
        }
        
        // Secondary: Effectiveness (higher is better)
        if (Math.abs(a.effectiveness - b.effectiveness) > 1) {
          return b.effectiveness - a.effectiveness;
        }
        
        // Tertiary: Total profit (higher is better)
        return b.totalProfit - a.totalProfit;
      })
      .slice(0, 10);
  }

  /**
   * Test a specific anti loss streak setting and calculate performance metrics
   */
  private static testAntiLossStreakSetting(
    csvData: ParsedCsvRow[],
    waitForLosses: number,
    stopAfterLosses: number,
    params: AnalysisParams,
    mode?: string
  ): AntiLossStreakOptimizationResult {
    // Apply anti loss streak rule with specified mode
    const testMode = mode || params.anti_loss_streak_mode;
    const result = AntiLossStreakService.applyAntiLossStreakRule(
      csvData,
      waitForLosses,
      stopAfterLosses,
      params.reset_on_win,
      testMode as 'consecutive_losses' | 'consecutive_trades'
    );

    // Get only non-skipped trades for calculations (matching manual analysis logic)
    const filteredTrades = result.trades.filter(trade => !trade.isSkipped);

    // Calculate performance metrics using the same logic as manual analysis
    let totalProfit = 0;
    let maxDrawdown = 0;
    let wins = 0;
    let peak = params.initial_capital;
    let runningEquity = peak;

    for (const trade of filteredTrades) {
      const pnl = trade.pnl;
      totalProfit += pnl;
      runningEquity += pnl;

      // Update peak and calculate drawdown from peak (matching manual analysis)
      peak = Math.max(peak, runningEquity);
      const drawdown = peak - runningEquity;
      maxDrawdown = Math.max(maxDrawdown, drawdown);

      if (pnl > 0) {
        wins++;
      }
    }

    const winRate = filteredTrades.length > 0 ? (wins / filteredTrades.length) * 100 : 0;
    const profitDDRatio = maxDrawdown > 0 ? totalProfit / maxDrawdown : totalProfit > 0 ? 999 : 0;
    
    // Calculate effectiveness as percentage of trades that would be protected
    const originalTrades = csvData.length;
    const skippedTrades = result.totalSkipped;
    const effectiveness = originalTrades > 0 ? (skippedTrades / originalTrades) * 100 : 0;

    return {
      optimalWaitForLosses: waitForLosses,
      optimalStopAfterLosses: stopAfterLosses,
      totalProfit,
      maxDrawdown,
      profitDDRatio,
      winRate,
      totalTrades: filteredTrades.length,
      skippedTrades,
      effectiveness
    };
  }

  /**
   * Compare all modes and return combined results
   */
  static compareAllModes(
    csvData: ParsedCsvRow[],
    maxWaitToTest: number,
    maxStopToTest: number,
    params: AnalysisParams
  ): AntiLossStreakTestResult[] {
    const modes = ['consecutive_losses', 'consecutive_trades'];
    const allResults: AntiLossStreakTestResult[] = [];
    
    for (const mode of modes) {
      const results = this.findTopAntiLossStreakSettings(csvData, maxWaitToTest, maxStopToTest, params, mode);
      allResults.push(...results);
    }
    
    // Sort combined results by performance
    return allResults
      .sort((a, b) => {
        if (Math.abs(a.profitDDRatio - b.profitDDRatio) > 0.1) {
          return b.profitDDRatio - a.profitDDRatio;
        }
        if (Math.abs(a.effectiveness - b.effectiveness) > 1) {
          return b.effectiveness - a.effectiveness;
        }
        return b.totalProfit - a.totalProfit;
      })
      .slice(0, 15); // Show more results for comparison
  }
}